/**
 * PinInput — segmented input for fixed-length codes (OTP, PIN, witness IDs).
 *
 * Each segment is a single character: typing in one slot focuses the
 * next; backspace in an empty slot focuses and clears the previous one;
 * pasting a full-length value distributes the characters across all
 * slots in one go. The component is fully controlled — the host owns
 * the `value` and is notified through `onChange` (full string) and
 * `onComplete` (called when every slot is populated).
 *
 * Single responsibility: the visible segmented input + keyboard
 * choreography. Validation (numeric only, alphanumeric, etc.) is the
 * caller's responsibility — pass a `mask` regex to filter accepted
 * characters.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useMemo, useRef } from "react";
import { GRAY, PRIMARY } from "../design-system/tokens.js";
import { cn } from "../styling/index.js";

export interface PinInputProps {
  /** Current value. Length must equal `length`. */
  readonly value: string;
  /** Number of slots. Defaults to 6. */
  readonly length?: number;
  /** Change handler — called with the full value string. */
  readonly onChange: (value: string) => void;
  /** Completion handler — called once when the value reaches full length. */
  readonly onComplete?: (value: string) => void;
  /**
   * Regex used to filter accepted characters. Defaults to `/[^0-9]/g`
   * (numeric only). The regex is applied with `replace(mask, "")` so
   * disallowed characters are silently dropped.
   */
  readonly mask?: RegExp;
  /** Whether each slot masks its character (password-style). Defaults to false. */
  readonly secret?: boolean;
  /** Disabled flag. */
  readonly disabled?: boolean;
  /** Accessible label for the segmented input as a whole. */
  readonly ariaLabel: string;
  /** Additional className applied to the wrapper. */
  readonly className?: string;
}

const SEGMENT_STYLE: React.CSSProperties = {
  background: "white",
  border: `1px solid ${GRAY[400]}`,
  borderRadius: "0.375rem",
  color: GRAY[900],
  fontFamily: "ui-monospace, monospace",
  fontSize: "1.25rem",
  fontWeight: 600,
  height: "2.5rem",
  outline: "none",
  textAlign: "center",
  width: "2.25rem",
};

const FOCUSED_BORDER = `2px solid ${PRIMARY[600]}`;

function segmentIndex(input: HTMLInputElement): number {
  const raw = input.dataset["cskPinSegment"];
  return raw === undefined ? 0 : Number.parseInt(raw, 10);
}

/** Renders a fixed-length segmented code input. */
export const PinInput = React.memo(function PinInput({
  value,
  length = 6,
  onChange,
  onComplete,
  mask,
  secret = false,
  disabled = false,
  ariaLabel,
  className,
}: PinInputProps): React.JSX.Element {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const resolvedMask = useMemo(() => mask ?? /[^0-9]/g, [mask]);

  const padded = useMemo(() => value.padEnd(length, " ").slice(0, length), [value, length]);
  const indexes = useMemo(() => Array.from({ length }, (_, index) => index), [length]);

  const writeAt = useCallback(
    (index: number, nextChar: string): string => {
      const characters = padded.split("");
      characters[index] = nextChar === "" ? " " : nextChar;
      return characters.join("").trimEnd();
    },
    [padded],
  );

  const focusAt = useCallback((index: number) => {
    const target = refs.current[index];
    if (target !== null && target !== undefined) target.focus();
  }, []);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const index = segmentIndex(event.currentTarget);
      const raw = event.target.value.replace(resolvedMask, "");
      if (raw.length === 0) {
        const next = writeAt(index, "");
        onChange(next);
        return;
      }
      // Take only the last typed character so the user can overwrite the slot.
      const nextChar = raw.slice(-1);
      const next = writeAt(index, nextChar);
      onChange(next);
      if (index + 1 < length) {
        focusAt(index + 1);
      }
      if (next.length === length) {
        onComplete?.(next);
      }
    },
    [focusAt, length, onChange, onComplete, resolvedMask, writeAt],
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const index = segmentIndex(event.currentTarget);
      const pasted = event.clipboardData.getData("text").replace(resolvedMask, "");
      if (pasted.length === 0) return;
      event.preventDefault();
      const fragments = padded.split("");
      let cursor = index;
      for (const character of pasted) {
        if (cursor >= length) break;
        fragments[cursor] = character;
        cursor += 1;
      }
      const next = fragments.join("").trimEnd();
      onChange(next);
      if (next.length >= length) {
        onComplete?.(next);
        focusAt(length - 1);
      } else {
        focusAt(Math.min(length - 1, cursor));
      }
    },
    [focusAt, length, onChange, onComplete, padded, resolvedMask],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const index = segmentIndex(event.currentTarget);
      if (event.key === "Backspace") {
        const current = padded[index];
        if (current === " " || current === undefined) {
          event.preventDefault();
          if (index > 0) {
            focusAt(index - 1);
            const next = writeAt(index - 1, "");
            onChange(next);
          }
        } else {
          event.preventDefault();
          const next = writeAt(index, "");
          onChange(next);
        }
      } else if (event.key === "ArrowLeft" && index > 0) {
        event.preventDefault();
        focusAt(index - 1);
      } else if (event.key === "ArrowRight" && index + 1 < length) {
        event.preventDefault();
        focusAt(index + 1);
      }
    },
    [focusAt, length, onChange, padded, writeAt],
  );

  const handleFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    event.currentTarget.style.border = FOCUSED_BORDER;
  }, []);

  const handleBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    event.currentTarget.style.border = `1px solid ${GRAY[400]}`;
  }, []);

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      data-csk-pin-input
      className={cn("inline-flex font-body", className)}
      style={{ gap: "0.45rem" }}
    >
      {indexes.map((index) => {
        const character = padded[index] === " " ? "" : (padded[index] ?? "");
        return (
          <input
            key={index}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type={secret ? "password" : "text"}
            inputMode={mask === undefined ? "numeric" : "text"}
            value={character}
            disabled={disabled}
            maxLength={1}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onFocus={handleFocus}
            onBlur={handleBlur}
            aria-label={`${ariaLabel} character ${index + 1} of ${length}`}
            data-csk-pin-segment={index}
            style={SEGMENT_STYLE}
          />
        );
      })}
    </div>
  );
});
